import { languageHelper } from './LanguageHelper';
import { AWTEventPriority, AWTEventProperties, AWTLogger, AWTLogManager, AWTPiiKind, AWTPropertyType } from '@aria/webjs-sdk';
import { browser, deviceType, os, deviceLanguage } from './EnvironmentHelper';
import { Dictionary, CommonProperty } from '../common/SharedTypes';
import {envConfig} from '../configuration/Configuration';

interface Property {
    key: CommonProperty;
    value: string | number | boolean | Date;
    piiKind?: AWTPiiKind;
    type?: AWTPropertyType;
}

enum LifecycleStage {
    PageLoad = "PageLoad",
    SilentLoginStarted = "SilentLoginStarted",
    SilentLoginFinished = "SilentLoginFinished",
    GenerateLink = "GenerateLink",
    CopyToClipboard = "CopyToClipboard",
    ClickPopupItemCopyToClipboard = "ClickPopupItemCopyToClipboard",
    ClickPopupItemShareToOutlook = "ClickPopupItemShareToOutlook",
    ClickPopupItemShareToGmail = "ClickPopupItemShareToGmail",
    GoToJoinLink = "GoToJoinLink"
}

class LoggerHelper {
    EcsLoadingFailed(result: { Error: any; }) {
        this._log({ 
            ElapsedSinceSessionStartInMs : this.GetTimeElapsedSinceSessionStart(),
            Error: result.Error
        }, AWTEventPriority.Normal, 'ecs_loading_success', 'EcsLoadingSuccess');
    }
    EcsLoadedSuccess() {
        this._log({ ElapsedSinceSessionStartInMs : this.GetTimeElapsedSinceSessionStart()}, AWTEventPriority.Normal, 'ecs_loading_success', 'EcsLoadingSuccess');
    }
  JoinLinkGenerationFailed(result: { IsGuest: boolean, StatusCode: number; Error: any; }) {
    const event = {
        ElapsedSinceSessionStartInMs : this.GetTimeElapsedSinceSessionStart(),
        StatusCode: result.StatusCode,
        IsGuest: result.IsGuest,
        ErrorMessage : result.Error,
    };      
    this._log(event, AWTEventPriority.Normal, 'generate_join_link_failed', 'GenerateJoinLinkFailed');
  }
  JoinLinkGenerationSucceeded(result: { IsGuest: boolean }) {
    const event = {
        ElapsedSinceSessionStartInMs : this.GetTimeElapsedSinceSessionStart(),
        IsGuest: result.IsGuest
    };      
    this._log(event, AWTEventPriority.Normal, 'generate_join_link_success', 'GenerateJoinLinkSucceeded');
  }  
  ClickedOnGoToGeneratedMeetingButton() {
    this._log({ ElapsedSinceSessionStartInMs : this.GetTimeElapsedSinceSessionStart()}, AWTEventPriority.Normal, 'clicked_button_gotogeneratedmeeting', 'ClickedOnGoToGeneratedMeeting');
    this.logLifecycleEvent(LifecycleStage.GoToJoinLink);
  }
  ClickedOnPopupItemShareViaOutlook() {
    this._log({ ElapsedSinceSessionStartInMs : this.GetTimeElapsedSinceSessionStart()}, AWTEventPriority.Normal, 'clicked_popupuitem_shareviaoutlook', 'ClickedOnPopupItemShareViaOutlook');
    this.logLifecycleEvent(LifecycleStage.ClickPopupItemShareToOutlook);
  }
  ClickedOnPopupItemShareViaGmail() {
    this._log({ ElapsedSinceSessionStartInMs : this.GetTimeElapsedSinceSessionStart()}, AWTEventPriority.Normal, 'clicked_popupuitem_shareviagmail', 'ClickedOnPopupItemShareViaGmail');
    this.logLifecycleEvent(LifecycleStage.ClickPopupItemShareToGmail);
  }
  ClickedOnPopupItemCopyToClipboard() {
    this._log({ ElapsedSinceSessionStartInMs : this.GetTimeElapsedSinceSessionStart()}, AWTEventPriority.Normal, 'clicked_popupuitem_copytoclipboard', 'ClickedOnPopupItemCopyToClipboard');
    this.logLifecycleEvent(LifecycleStage.ClickPopupItemCopyToClipboard);    
  }

  ClickedOnGenerateJoinLinkButton() {
    this._log({ ElapsedSinceSessionStartInMs : this.GetTimeElapsedSinceSessionStart()}, AWTEventPriority.Normal, 'clicked_button_generatejoinlink', 'ClickedGenerateJoinLink');
    this.logLifecycleEvent(LifecycleStage.GenerateLink);
  }

  public SilentLoginFinishedError(error: any) {
    const diffMs = (new Date().valueOf() - this._silentLoginStart.valueOf());
    this.Log("Silent login got error:", error);
    const event = {
        Duration : diffMs,
        ErrorMessage : error,
        isSuccess: false
    };
    this._log(event, AWTEventPriority.Normal, 'silentlogin_finished_failure', 'SilentLoginFinishedFailure');
    this.logLifecycleEvent(LifecycleStage.SilentLoginFinished);
  }

  public SilentLoginStarted() {
    this._silentLoginStart = new Date();
    this._log({}, AWTEventPriority.Normal, 'silentlogin_started', 'SilentLoginStarted');
    this.logLifecycleEvent(LifecycleStage.SilentLoginStarted);
  }
  public SilentLoginFinishedSuccess() {
    this._loggedIn = true;
    this.Log("Silent login got token");
    const diffMs = (new Date().valueOf() - this._silentLoginStart.valueOf());
    const event = {
        Duration : diffMs,
        isSuccess: true
    };
    this._log(event, AWTEventPriority.Normal, 'silentlogin_finished_ok', 'SilentLoginFinishedOk');
    this.logLifecycleEvent(LifecycleStage.SilentLoginFinished);
  }

  public ClickedOnCopyJoinLinkToClipboard() {
    this._log({ ElapsedSinceSessionStartInMs : this.GetTimeElapsedSinceSessionStart()}, AWTEventPriority.Normal, 'clicked_copytoclipboard', 'ClickedCopyToClipboard');
    this.logLifecycleEvent(LifecycleStage.CopyToClipboard);
  }

  private logLifecycleEvent(stage: LifecycleStage) {
    this._log({ 
        ElapsedSinceSessionStartInMs : this.GetTimeElapsedSinceSessionStart(),
        Stage: stage,
    }, AWTEventPriority.Immediate_sync, 'lifecycle_event', 'LifecycleEvent');
  }

  private sessionStarted = (priority: AWTEventPriority = AWTEventPriority.Normal) => {
    this._sessionStartDate = new Date();
    this._log({}, priority, 'session_started', 'SessionStarted');
    this.logLifecycleEvent(LifecycleStage.PageLoad);
    }

    private _logger: AWTLogger = AWTLogManager.getLogger();
    private _sessionStartDate: Date = new Date();
    private _silentLoginStart: Date = new Date();
    private _sessionId: string = '';
    private _loggedIn: boolean = false;
    private prefix: string = 'meet_now_';
    private _commonProperties: Dictionary<Property> = {};
    private _language: string = 'en-US';

    init() {

        this.Log('Initializing the logger. ', envConfig.Environment);
        this._language = languageHelper.GetCurrentLanguage();

        AWTLogManager.initialize(envConfig.AriaIngestionKey, {
            enableAutoUserSession: true,
        });
        this._sessionId = this._logger.getSessionId();
        window.onerror = this._onError;
        this.sessionStarted();
        this.Log('Initialized the logger with session id ', this._sessionId );
    }

    public Log(message?: any, ...optionalParams: any[]) {
        if (envConfig.AllowConsoleLogging) {
            if (optionalParams && optionalParams.length > 0)
                console.log(message, optionalParams);
            else
                console.log(message);
        }
    }

    private _onError = (message: any, file: any, line: any, col: any, error: any) => {
        this._log({
            message,
            file,
            line,
            col,
            error,
        }, AWTEventPriority.Normal, 'js_onerror', 'Error');
    }

    private GetTimeElapsedSinceSessionStart(): number {
        return (new Date().valueOf() - this._sessionStartDate.valueOf());
    }

    private setName = (eventProperties: AWTEventProperties, name: string) => {
        eventProperties.setName(this.prefix + name);
        eventProperties.setType(name);
    }

    private setCommonProperties = (eventProperties: AWTEventProperties) => {
        Object.keys(this._commonProperties).forEach((key) => {
            const property = (this._commonProperties as any)[key] as Property;
            if (property.piiKind) {
                eventProperties.setPropertyWithPii(property.key, property.value, property.piiKind);
            } else {
                eventProperties.setProperty(property.key, property.value);
            }
        });
        eventProperties.setProperty('DeviceInfo.Custom.Os', os);
        eventProperties.setProperty('DeviceInfo.Custom.Browser', browser);
        eventProperties.setProperty('DeviceInfo.Custom.DeviceType', deviceType);
        eventProperties.setProperty('Config.Env', envConfig.Environment);
        eventProperties.setProperty('UILanguage', this._language);
        eventProperties.setProperty('DetectedDeviceLanguage', deviceLanguage);
        eventProperties.setProperty('LoggedIn', this._loggedIn);
    }

    private setProperties = (eventProperties: AWTEventProperties, properties: any, propertyPrefix: string) => {
        Object.keys(properties).forEach((property) => {
            if (typeof properties[property] === 'object' && properties[property]) {
                this.setProperties(eventProperties, properties[property], propertyPrefix + '.' + property);
            } else {
                eventProperties.setProperty(propertyPrefix + '.' + property, properties[property]);
            }
        });
    }

    private _log(event: any, priority: AWTEventPriority, name: string, propertyPrefix: string) {
        const eventProperties = new AWTEventProperties();
        eventProperties.setEventPriority(priority);
        this.setName(eventProperties, name);
        this.setCommonProperties(eventProperties);
        this.setProperties(eventProperties, event, propertyPrefix);
        this._logger.logEvent(eventProperties);
    }

}

export const logger = new LoggerHelper();
